import type { CSSProperties, ReactNode } from 'react';

/**
 * Skeleton com shimmer — substitui placeholders de loading.
 *
 * Visual:
 *  - Base: cinza escuro sutil casando com o tema
 *  - Shimmer: gradient diagonal varrendo da esquerda pra direita em loop
 *  - Suporte a borderRadius custom, shape (rect/circle/text)
 *  - Componentes compostos: <Skeleton.Card>, <Skeleton.Avatar>, <Skeleton.Line>
 *
 * Implementação:
 *  - Background-image gradient + background-position animado via CSS keyframes
 *  - O keyframe `sk-shimmer` está definido no globals.css (adiciona se faltar)
 */

interface SkeletonProps {
  /** Width em px ou string CSS. Default '100%'. */
  width?: number | string;
  /** Height em px ou string CSS. Default 16. */
  height?: number | string;
  /** Shape rápido. 'circle' aplica borderRadius:50%. */
  shape?: 'rect' | 'circle' | 'text';
  /** Override de borderRadius. */
  borderRadius?: number | string;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

const shimmerStyle: CSSProperties = {
  // Base + shimmer overlay. 200% width pra background-position deslocar suavemente.
  backgroundImage: `
    linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.04) 0%,
      rgba(255, 255, 255, 0.04) 35%,
      rgba(255, 255, 255, 0.10) 50%,
      rgba(255, 255, 255, 0.04) 65%,
      rgba(255, 255, 255, 0.04) 100%
    )
  `,
  backgroundColor: 'rgba(255, 255, 255, 0.03)',
  backgroundSize: '200% 100%',
  animation: 'sk-shimmer 1.4s linear infinite',
  willChange: 'background-position',
};

export function Skeleton({
  width = '100%',
  height = 16,
  shape = 'rect',
  borderRadius,
  className = '',
  style,
  children,
}: SkeletonProps) {
  const br =
    borderRadius !== undefined
      ? typeof borderRadius === 'number'
        ? `${borderRadius}px`
        : borderRadius
      : shape === 'circle'
        ? '50%'
        : shape === 'text'
          ? '4px'
          : '8px';
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      className={className}
      style={{
        ...shimmerStyle,
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
        borderRadius: br,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Atalho: linha de texto skeleton. */
Skeleton.Line = function SkeletonLine({
  width = '100%',
  height = 14,
  className,
  style,
}: Pick<SkeletonProps, 'width' | 'height' | 'className' | 'style'>) {
  return (
    <Skeleton
      width={width}
      height={height}
      shape="text"
      className={className}
      style={style}
    />
  );
};

/** Atalho: avatar circular. */
Skeleton.Avatar = function SkeletonAvatar({
  size = 40,
  className,
  style,
}: {
  size?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <Skeleton
      width={size}
      height={size}
      shape="circle"
      className={className}
      style={style}
    />
  );
};

/** Atalho: card com várias linhas + borda. */
Skeleton.Card = function SkeletonCard({
  className = '',
  height = 120,
}: {
  className?: string;
  height?: number;
}) {
  return (
    <div
      className={`rounded-2xl border border-white/8 bg-white/[0.02] p-4 ${className}`}
      style={{ minHeight: height }}
    >
      <div className="flex items-start gap-3">
        <Skeleton.Avatar size={44} />
        <div className="flex-1 space-y-2">
          <Skeleton.Line width="60%" height={14} />
          <Skeleton.Line width="40%" height={12} />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <Skeleton.Line width="100%" height={10} />
        <Skeleton.Line width="80%" height={10} />
      </div>
    </div>
  );
};
